/**
 * The registry builder: `folder()` -> `tool()` -> `command()` composes backslash paths and
 * registers every entry. Parents are explicit (a child is only reachable from its parent node), so
 * a registered entry always has a declared parent.
 *
 * Plain folders (`Admin\Reports`, `CLI\API Keys`) are containers: they carry no description, are
 * not permissions and expose no `permission` (E8).
 *
 * Nodes are frozen, so a node's `path` cannot be rewritten to make later children register under an
 * undeclared parent (red-team F-5). Declaring is only possible until the registry is sealed, i.e.
 * from `./declarations/` (see `registry.ts`).
 */
import { PATH_SEPARATOR } from './path-rules';
import { findEntry, register } from './registry';
import type { FolderOptions, NodeOptions, PermissionKind, SecurityPermission } from './types';

interface ChildFactory {
  /** A plain container folder. */
  folder(label: string, options?: FolderOptions): FolderNode;
  /** A tool (or a sub-tool); carries a permission. */
  tool(label: string, options?: NodeOptions): PermissionNode;
  /** A command or action gate; carries a permission. */
  command(label: string, options?: NodeOptions): PermissionNode;
  /** A report data table (`Data Table: X`); carries a permission. */
  dataTable(label: string, options?: NodeOptions): PermissionNode;
}

export interface FolderNode extends ChildFactory {
  readonly path: string;
  readonly kind: 'folder' | 'dataSecurity';
}

export interface PermissionNode extends ChildFactory {
  readonly path: string;
  readonly kind: 'tool' | 'command' | 'dataTable';
  readonly permission: SecurityPermission;
}

type Node = FolderNode | PermissionNode;

/** Module-private brand constructor; `register()` refuses to record anything once sealed. */
const asPermission = (path: string): SecurityPermission => path as SecurityPermission;

function checkLabel(label: string): void {
  if (label.trim().length === 0) throw new Error('Security registry: a label must not be empty.');
  if (label !== label.trim()) throw new Error(`Security registry: label "${label}" has leading or trailing whitespace.`);
  if (label.includes(PATH_SEPARATOR)) throw new Error(`Security registry: label "${label}" must not contain a backslash.`);
}

function declare(
  parent: Node | null,
  kind: PermissionKind,
  label: string,
  options: NodeOptions & FolderOptions,
): Node {
  checkLabel(label);
  const isContainer = kind === 'folder' || kind === 'dataSecurity';
  if (isContainer && options.description !== undefined) {
    throw new Error(`Security registry: folder "${label}" must not carry a description (E8).`);
  }
  if (parent && !isContainer && parent.kind === 'dataSecurity' && kind !== 'dataTable') {
    throw new Error(`Security registry: "${label}" under Data Security must be a data table or a family.`);
  }
  if (parent && kind === 'folder' && parent.kind !== 'folder') {
    throw new Error(`Security registry: folder "${label}" can only be declared under a folder.`);
  }
  const path = parent ? parent.path + PATH_SEPARATOR + label : label;
  const permission = isContainer ? undefined : asPermission(path);
  register({
    path,
    label,
    ...(options.description === undefined ? {} : { description: options.description }),
    kind,
    parent: parent ? parent.path : null,
    protectedFromInactivation: options.protectedFromInactivation ?? false,
    uiOnly: options.uiOnly ?? false,
    ...(permission === undefined ? {} : { permission }),
    ...(options.icon === undefined ? {} : { icon: options.icon }),
    reportOwned: options.reportOwned ?? (parent ? (findEntry(parent.path)?.reportOwned ?? false) : false),
  });
  return makeNode(path, kind, permission);
}

function makeNode(path: string, kind: PermissionKind, permission: SecurityPermission | undefined): Node {
  const self = { path, kind, ...(permission === undefined ? {} : { permission }) } as Node;
  Object.assign(self, {
    folder: (label: string, options: FolderOptions = {}) => declare(self, 'folder', label, options) as FolderNode,
    tool: (label: string, options: NodeOptions = {}) => declare(self, 'tool', label, options) as PermissionNode,
    command: (label: string, options: NodeOptions = {}) => declare(self, 'command', label, options) as PermissionNode,
    dataTable: (label: string, options: NodeOptions = {}) =>
      declare(self, 'dataTable', label, options) as PermissionNode,
  });
  return Object.freeze(self);
}

/** Declares a top-level plain folder. */
export function folder(label: string, options: FolderOptions = {}): FolderNode {
  return declare(null, 'folder', label, options) as FolderNode;
}

/** Declares a top-level tool, for a root path a route enforces directly (e.g. `Calculator`). */
export function tool(label: string, options: NodeOptions = {}): PermissionNode {
  return declare(null, 'tool', label, options) as PermissionNode;
}

export const DATA_SECURITY_ROOT = 'Data Security';

let dataSecurityNode: FolderNode | undefined;

/**
 * The `Data Security` root, idempotent: the first call declares it (kind `dataSecurity`), later
 * calls return it. `family()` and the seeded data-table declarations both attach below it.
 */
export function dataSecurityRoot(): FolderNode {
  if (dataSecurityNode) return dataSecurityNode;
  if (findEntry(DATA_SECURITY_ROOT)) {
    throw new Error(`Security registry: "${DATA_SECURITY_ROOT}" was declared outside dataSecurityRoot().`);
  }
  dataSecurityNode = declare(null, 'dataSecurity', DATA_SECURITY_ROOT, {}) as FolderNode;
  return dataSecurityNode;
}

/** Declares `Data Security\{securityType}` once, as a container for that family's leaves. */
export function declareFamilyFolder(securityType: string): FolderNode {
  return declare(dataSecurityRoot(), 'dataSecurity', securityType, {}) as FolderNode;
}
